import React from 'react'
import axios from 'shared/lib/axios'
import { flashSuccessMessage, flashErrorMessage } from 'redux-flash'

export const ORDER_DETAIL_FETCH = 'ORDER_DETAIL_FETCH'
export const ORDER_DETAIL_RESPONSE = 'ORDER_DETAIL_RESPONSE'
export const WARRANTY_PRODUCT_RESPONSE = 'WARRANTY_PRODUCT_RESPONSE'
export const ORDER_DETAIL_CANCEL = 'ORDER_DETAIL_CANCEL'
export const ORDER_DETAIL_ADMIN_FETCH = 'ORDER_DETAIL_ADMIN_FETCH'
export const SUBSCRIPTION_CANCEL = 'SUBSCRIPTION_CANCEL'

export function orderDetailResponse(json) {
  return { type: ORDER_DETAIL_RESPONSE, order: json }
}

export function orderDetailFetch(id) {
  return (dispatch) => {
    dispatch({ type: ORDER_DETAIL_FETCH })
    return axios
      .get(`/api/order/${id}`)
      .then((json) => dispatch(orderDetailResponse(json.data)))
  }
}

export function orderDetailAdmin(id) {
  return (dispatch) =>
    axios.get(`/api/admin/orders/${id}`).then((json) => {
      dispatch({
        type: ORDER_DETAIL_RESPONSE,
        order: json.data.orders[0].order,
      })
    })
}

export function orderDetailUpdate(orderId, payload) {
  return (dispatch) =>
    axios
      .patch(`/api/admin/orders/${orderId}`, payload)
      .then((json) => {
        dispatch({
          type: ORDER_DETAIL_RESPONSE,
          order: json.data,
        })
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err)
        const responseError = err?.response?.data
        let errorMessage = 'Order update failed'
        if (responseError && responseError.message) {
          if (responseError.message._schema) {
            errorMessage = responseError.message._schema[0]
          } else {
            errorMessage = responseError.message[0]
          }
        }
        dispatch(flashErrorMessage(errorMessage))
      })
}

export function orderDetailCancel(id, options = {}) {
  return async (dispatch) => {
    dispatch({ type: ORDER_DETAIL_CANCEL })

    try {
      await axios.post(`/api/internal_order_cancelled/${id}`, options)

      dispatch(orderDetailFetch(id))
      dispatch(flashSuccessMessage('Order has been cancelled.'))

      return { success: true }
    } catch (e) {
      const data = e?.response?.data
      let errorMessage = data?.message || 'Order cancellation failed'

      if (data?.force_cancellation_allowed) {
        errorMessage = [
          errorMessage,
          <br key="br" />,
          'If you would like to force this cancellation, click Cancel Order again and select the Force Cancel option.',
        ]
      }

      dispatch(flashErrorMessage(errorMessage))

      return {
        success: false,
        forceCancellationAllowed: data?.force_cancellation_allowed,
      }
    }
  }
}

export function subscriptionCancel(id) {
  return (dispatch) => {
    dispatch({ type: SUBSCRIPTION_CANCEL })
    return axios
      .delete(`/api/subscriptions/${id}`)
      .then((json) => dispatch(flashSuccessMessage(json.data.message)))
      .then(() => dispatch(orderDetailFetch(id)))
      .catch(() =>
        dispatch(flashErrorMessage('Subscription cancellation failed'))
      )
  }
}

export function warrantyIssuedCancel(orderId, warrantyIssuedId) {
  return (dispatch) => {
    const payload = {
      warranty_issued_id: warrantyIssuedId,
      cancellation_reason: 'cancelled by retailer',
    }
    return axios
      .post('/api/cancel_warranty_issued', payload)
      .then(() => dispatch(orderDetailFetch(orderId)))
      .then(() => dispatch(flashSuccessMessage('Warranty has been cancelled.')))
      .catch(() => dispatch(flashErrorMessage('Warranty cancellation failed')))
  }
}

export const warrantyIssuedReinstate = (orderId, warrantyIssuedId) => {
  return (dispatch) => {
    return axios
      .patch(`/api/warranties/${warrantyIssuedId}/reinstate`)
      .then((response) => {
        dispatch(orderDetailFetch(orderId))
        dispatch(
          flashSuccessMessage(
            response.data?.message || 'Warranty has been reinstated.'
          )
        )
      })
      .catch((err) => {
        const message =
          err.response?.data?.message || 'Warranty reinstate failed'
        dispatch(flashErrorMessage(message))
      })
  }
}

export function warrantyProductUpdate(orderId, warrantyIssuedId, payload) {
  return (dispatch) =>
    axios
      .patch(
        `/api/admin/orders/${orderId}/warranty_issued/${warrantyIssuedId}`,
        payload
      )
      .then((json) => {
        dispatch({
          type: WARRANTY_PRODUCT_RESPONSE,
          warranty: json.data,
        })
      })
      .catch(() => {
        dispatch(flashErrorMessage('Product update failed'))
      })
}

export const ORDER_EDIT_STATUS = Object.freeze({
  ADDED: 'added',
  BUNDLED: 'bundled',
  REPRICED: 'repriced',
  SKIPPED_EXISTING_COVERAGE: 'skipped_existing_coverage',
  NO_VALID_OFFER: 'no_valid_offer',
})

const ADDED_STATUSES = [
  ORDER_EDIT_STATUS.ADDED,
  ORDER_EDIT_STATUS.BUNDLED,
  ORDER_EDIT_STATUS.REPRICED,
]

export function orderAddItems(externalOrderId, orderId, payload) {
  return async (dispatch) => {
    let response
    try {
      // No Authorization header: core resolves the retailer from the session,
      // and the public token ProductForm uses for offer lookup would 401.
      response = await axios.put(
        `/api/order-detail/${encodeURIComponent(externalOrderId)}`,
        payload
      )
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to add items to the order'
      dispatch(flashErrorMessage(message))
      return { success: false, partial: false, results: [], error: message }
    }

    try {
      await dispatch(orderDetailFetch(orderId))
    } catch {
      // The add succeeded; a failed refresh must not report as a failed add.
    }

    const {
      success = false,
      partial = false,
      results = [],
    } = response.data ?? {}
    const allSucceeded =
      results.length > 0 &&
      results.every(({ status }) => ADDED_STATUSES.includes(status))

    if (allSucceeded) {
      dispatch(flashSuccessMessage('Items have been added to the order.'))
    }

    return { success, partial, results, allSucceeded }
  }
}
